import { Component, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Observable, Subject, forkJoin } from "rxjs";

import { Router, ActivatedRoute } from "@angular/router";

import { Certification } from "./../../shared/models/products/certification";
import { CertificationPayment } from "./../../shared/models/payments/certification-payment";
import { PurchaseService } from "./../../shared/services/purchase.service";
import { CommonApiService } from "./../../../shared/services/api/common-api.service";

import { Profile } from "./../../../practitioner/shared/models/profile";
import { RegexPatterns } from "./../../../shared/regex/regex-patterns";
import { AppSettingsService } from "./../../../shared/services/app-settings.service";
import { NotificationsService } from "./../../../shared/services/notifications.service";
import { UserService } from "./../../../shared/services/user.service";
import { ViewStateService } from "./../../../shared/services/view-state.service";
import { PurchaseStateService } from "./../../shared/services/purchase-state.service";
import { Purchase } from "../../shared/models/purchase";
import { debounceTime, takeUntil } from "rxjs/operators";

import { AuthService } from "app/auth/auth.service";
import { environment } from "environments/environment";

enum CardTypes {
  Amex,
  Discover,
  MasterCard,
  Visa,
}

declare var Accept: any;

@Component({
  selector: "app-certification",
  templateUrl: "./certification.component.html",
})
export class CertificationComponent implements OnInit, OnDestroy {
  isRecertify: boolean;

  checkoutForm: UntypedFormGroup;
  serverMessage: string;
  certification: Certification;

  cardTypes = CardTypes;
  ccType: CardTypes;
  countries: any;
  purchase = new Purchase(this.userService);
  practitionerName: string;
  ccpInfo: any;

  private destroy$ = new Subject<void>();

  constructor(
    private purchaseState: PurchaseStateService,
    private purchaseService: PurchaseService,
    private commonApiService: CommonApiService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private appSettings: AppSettingsService,
    private notificationsService: NotificationsService,
    private userService: UserService,
    private viewState: ViewStateService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.viewState.startLoading();
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.isRecertify = params["isRecertify"] === "true";
      });

    this.buildForm();
    this.getCountries();
    forkJoin([
      this.purchaseService.getCCPInfo(this.userService.CurrentUser.personId),
      this.purchaseService.getProfile(this.userService.CurrentUser.personId),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.ccpInfo = res[0];
        let fee = this.ccpInfo.feeDue;

        let profile = <Profile>res[1];
        this.formValues.address = profile.line1;
        this.formValues.nameOnCard = profile.firstName
          .concat(" ")
          .concat(profile.lastName);
        this.formValues.city = profile.city;
        this.formValues.state = profile.stateProvince;
        this.formValues.zip = profile.zip;
        this.formValues.countryId = profile.countryId.toString();

        this.checkoutForm.patchValue(this.formValues);

        this.certification = new Certification();

        if (this.ccpInfo.recertify) this.certification.title = "Recertify";
        else if (this.ccpInfo.reinstate && !this.ccpInfo.feeOverrideAmount)
          this.certification.title = "Reinstate Certification";
        else if (this.ccpInfo.feeOverrideAmount) {
          this.certification.title = "Reinstatement Exception Amount";
          fee = this.ccpInfo.feeOverrideAmount;
        } else this.certification.title = "Certification";

        this.certification.quantity = 1;
        this.certification.price = fee;

        //console.log(this.certification);
        this.purchase.products.push(this.certification);
        this.practitionerName = profile.firstName
          .concat(" ")
          .concat(profile.lastName);

        if (fee === 0) {
          this.processCCP(null);
          this.isRecertify = false; // suppresses the recert warning
          return;
        }
        this.purchaseService
        .loadAcceptJs()
        .then(() => {
          console.log("Accept.js is ready");
        })
        .catch((err) => {
          console.error(err);
        });
        this.viewState.stopLoading();
      });

    this.checkoutForm.controls["cardNumber"].valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((v: string) => {
        if (v.match("^4[0-9]{12}(?:[0-9]{3})?$")) {
          this.ccType = CardTypes.Visa;
        } else if (
          v.match(
            "^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$"
          )
        ) {
          this.ccType = CardTypes.MasterCard;
        } else if (v.match("^3[47][0-9]{13}$")) {
          this.ccType = CardTypes.Amex;
        } else if (v.match("^6(?:011|5[0-9]{2})[0-9]{12}$")) {
          this.ccType = CardTypes.Discover;
        } else {
          this.ccType = null;
        }
      });
  }

  private submitPaymentInitial() {
      const authData = {
        clientKey: environment.clientKey,
        apiLoginID: environment.apiLogin,
      };
  
      const cardData = {
        cardNumber: this.formValues.cardNumber,
        month: this.formValues.expirationDate.substring(0, 2),
        year: 20 + this.formValues.expirationDate.substring(2),
        cardCode: this.formValues.ccv,
      };
  
      const secureData = {
        authData,
        cardData,
      };
      //console.log(authData);
      //console.log(cardData);
  
      Accept.dispatchData(secureData, this.responseHandler.bind(this));
    }
  
    responseHandler(response: any) {
      if (response.messages.resultCode === "Error") {
        console.error(
          "Error creating payment nonce/token:",
          response.messages.message
        );
        console.log(response.messages);
        let err = response.messages.message[0].text;
        if (!err || err=="") 
          err = "Please review credit card information and resubmit";
        this.notificationsService.error(
          "Payment Failed",
          err
        );        
        this.viewState.stopLoading();
        return;
      }
  
      const opaqueData = response.opaqueData;
      
      this.processCCP(opaqueData);
    }

  onSubmit(updatedForm) {
    this.formValues = updatedForm;
    this.validateForm();
    if (this.checkoutForm.valid) {
      this.viewState.startLoading();
      this.purchase.paymentMethod = "Credit Card";
      this.purchase.paymentDate = new Date();

      this.submitPaymentInitial();
    }
  }

  onCancel(event: Event) {
    event.preventDefault();
    this.purchase = new Purchase(this.userService);
    this.router.navigate([""]);
  }

  private getCountries() {
    this.commonApiService
      .getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.countries = res;
        },
        (error) => {
          console.log(error);
        }
      );
  }

  private handleServerErrorResponse(response) {
    if (response.status === 500) {
      this.notificationsService.error(
        "Unexpected Error",
        "An unexpected error occurred."
      );
      if (!this.appSettings.IsProductionMode) console.log(response);
    } else {
      this.mapServerErrorsToForm(response.error);
    }

    this.viewState.stopLoading();
  }

  private mapServerErrorsToForm(errors) {
    let unknownErrors = [];
    let firstErrorFieldName;

    for (let error of errors) {
      this.formErrors[error.fieldName] = "";

      const control = this.checkoutForm.get(error.fieldName);
      if (control) {
        if (!firstErrorFieldName) firstErrorFieldName = error.fieldName;
        this.formErrors[error.fieldName] += error.message + " ";
      } else {
        unknownErrors.push(error.message);
      }
    }

    if (!errors || errors.message) {
      this.serverMessage = errors ? errors.message : null;
    } else if (unknownErrors.length > 0) {
      this.serverMessage = unknownErrors.join(" ");
    }

    if (!this.serverMessage)
      this.serverMessage =
        "Your transaction could not be processed. Please try again or contact NBRC.";

    this.viewState.stopLoading();

    this.notificationsService.error(
      "Payment Failed",
      "Your transaction was not processed. Please check your payment information and try again or contact NBRC."
    );

    setTimeout(function () {
      const target = <HTMLElement>(
        (document.getElementById(firstErrorFieldName)
          ? document.getElementById(firstErrorFieldName)
          : document.getElementById("serverMessage"))
      );
      console.log(target);
      target.scrollIntoView();
    }, 750);
  }

  private processCCP(opaqueData) {
    let payment = new CertificationPayment();
    payment.personId = this.userService.CurrentUser.personId;
    payment.paymentType = "CP";
    payment.TotalCost = 0;
    payment.salesTax = 0;

    if (this.purchase.total > 0) {
      payment.cardCode = ""//this.formValues.ccv;
      payment.cardNumber = ""//this.formValues.cardNumber;
      payment.Address = this.formValues.address;
      payment.City = this.formValues.city;
      payment.expirationDate = ""//this.formValues.expirationDate.replace("/", "");
      payment.payerName = this.formValues.nameOnCard;
      payment.paymentType = "CC";
      payment.salesTax = 0;
      payment.State = this.formValues.state;
      payment.countryId = this.formValues.countryId;
      payment.TotalCost = this.purchase.total;
      payment.Zip = this.formValues.zip;
      payment.dataValue = opaqueData.dataValue;
      payment.dataDescriptor = opaqueData.dataDescriptor;
    }

    this.purchaseService
      .sendCertificationPayment(payment)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.certification.isAuditRequired = res.flagForAudit;
          this.purchase.products = [];
          this.purchase.products.push(this.certification);
          this.purchaseState.setCompletedPurchase(this.purchase);
          this.purchase = new Purchase(this.userService);

          this.viewState.stopLoading();
          this.isRecertify = false; // success clear the flag
          this.authService
            .refreshToken()
            .pipe(takeUntil(this.destroy$))
            .subscribe((rt) => {
              if (this.certification.isAuditRequired)
                this.router.navigate(["purchase/checkout/confirmation"]);
              else
                this.router.navigate(["/purchase/products"], {
                  queryParams: { recert: "true" },
                });
            });
        },
        (e) => {
          this.handleServerErrorResponse(e);
          this.viewState.stopLoading();
        }
      );
  }

  private buildForm() {
    this.checkoutForm = this.formBuilder.group({
      nameOnCard: [this.formValues.nameOnCard, [Validators.required]],
      cardNumber: [
        this.formValues.cardNumber,
        [
          Validators.required,
          Validators.minLength(15),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      expirationDate: [this.formValues.expirationDate, [Validators.required]],
      ccv: [
        this.formValues.ccv,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      address: [this.formValues.address, [Validators.required]],
      city: [this.formValues.city, [Validators.required]],
      state: [this.formValues.state, [Validators.required]],
      zip: [this.formValues.zip, [Validators.required]],
      countryId: [this.formValues.countryId, [Validators.required]],
    });
  }

  private validateForm() {
    this.serverMessage = null;

    for (const control in this.checkoutForm.controls) {
      this.checkoutForm.controls[control].updateValueAndValidity();
    }

    let hasErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.checkoutForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }
    if (hasErrors) this.viewState.stopLoading();
  }

  formErrors = {
    nameOnCard: "",
    cardNumber: "",
    expirationDate: "",
    ccv: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    countryId: "",
  };

  private validationMessages = {
    nameOnCard: {
      required: "Name on Card is required.",
    },
    cardNumber: {
      required: "Card Number is required.",
      minlength: "Card Number is invalid.",
      pattern: "Card Number is invalid.",
    },
    expirationDate: {
      required: "Expiration is required.",
    },
    ccv: {
      required: "CCV is required.",
      minlength: "CCV is invalid.",
      pattern: "CCV is invalid.",
    },
    address: {
      required: "Address is required.",
    },
    city: {
      required: "City is required.",
    },
    state: {
      required: "State is required.",
    },
    zip: {
      required: "Postal Code is required.",
    },
    countryId: {
      required: "Country is required.",
    },
  };

  private formValues = {
    nameOnCard: "",
    cardNumber: "",
    expirationDate: "",
    ccv: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    countryId: "",
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
